import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import type {
  ComponentProps,
  FormEventHandler,
} from "react"
import {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react"
import { PersonalInformationForm } from "src/components"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import {
  useAccount,
  useUpdateAccount,
  useUpdateFollow,
} from "src/hooks"
import { GenericErrorToast } from "src/toasts"
import {
  getLocaleDateStringFromDate,
  getDateFromLocaleDateString,
} from "src/utils"

type Form = ComponentProps<typeof PersonalInformationForm>["form"]

interface Props {
  onSubmitted: FormEventHandler<HTMLFormElement>;
  children?: ComponentProps<typeof PersonalInformationForm>["children"];
}

export function PersonalInformationPage(props: Props) {
  const {
    onSubmitted,
    children,
  } = props
  const { showToast } = useToasterContext()
  const polyglot = usePolyglot()

  const { data: account } = useAccount()
  const { selfFollow } = useFollowedUsers()
  const updateAccount = useUpdateAccount()
  const updateFollow = useUpdateFollow()

  const initialValue: Form = useMemo(
    () => {
      const dateOfBirth = account?.birthdate
        ? getLocaleDateStringFromDate(
          polyglot.locale(),
          dayjs(account.birthdate).toDate(),
        )
        : ""

      return {
        firstname: account?.firstname ?? "",
        lastname: account?.lastname ?? "",
        dateOfBirth,
        nickname: account?.nickname ?? "",
      }
    },
    [
      account,
      polyglot,
    ],
  )

  const [
    form,
    setForm,
  ] = useState<Form>(initialValue)

  const updateForm = useCallback(
    (value: Partial<Form>) => {
      setForm({
        ...form,
        ...value,
      })
    },
    [
      form,
      setForm,
    ],
  )

  useEffect(
    () => {
      setForm(initialValue)
    },
    [
      initialValue,
      setForm,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        try {
          if (!updateAccount || !updateFollow || !account || !selfFollow) {
            throw new Error("cannot update account")
          }

          const dateOfBirth = dayjs(
            getDateFromLocaleDateString(
              polyglot.locale(),
              form.dateOfBirth,
            ),
          ).format("YYYY/MM/DD")

          await updateAccount({
            id: account.id,
            firstname: form.firstname,
            lastname: form.lastname,
            birthdate: dateOfBirth,
          })

          await updateFollow({
            id: selfFollow.id,
            nickname: form.nickname || null,
          })

          onSubmitted(ev)
        } catch {
          showToast(
            "edit-personal-info-error-toast",
            GenericErrorToast,
          )
        }
      })()
    },
    [
      account,
      form,
      selfFollow,
      updateAccount,
      updateFollow,
      onSubmitted,
      showToast,
      polyglot,
    ],
  )

  return (
    <PersonalInformationForm
      form={form}
      updateForm={updateForm}
      onSubmit={onSubmit}
    >
      {children}
    </PersonalInformationForm>
  )
}
